import { compose } from 'redux';
import { connect } from 'react-redux';
import { getFormValues, change, submit } from 'redux-form';
import _debounce from 'lodash/debounce';
import { withForm } from 'components/Form';
import { PERMISSION } from 'constants/logic';
import { selectActiveVenueId, selectUserHasPermission, selectUserId } from 'selectors/general';
import { selectIsDesktop } from 'selectors/ui';
import { DEFAULT_CLASSPASS_SPOTS_STEP } from 'helpers/classpass-spots-allocation';
import {
  addSpot,
  updateScheduleNetworkAllocation as updateScheduleNetworkAllocationAction,
} from '../YieldManagement.saga';
import ReservationsOverview from './ReservationsOverview';

const NETWORK_CAP_DEBOUNCE_WAIT = 1000;
const SS_ADD_SPOT_VALUE = 1;

const debouncedSubmit = _debounce((action, ...args) => action(...args), NETWORK_CAP_DEBOUNCE_WAIT, {
  leading: false,
  trailing: true,
});

const decrementClasspassSpots = currentClasspassSpots => {
  return currentClasspassSpots - DEFAULT_CLASSPASS_SPOTS_STEP;
};

const incrementClasspassSpots = currentClasspassSpots => {
  return currentClasspassSpots + DEFAULT_CLASSPASS_SPOTS_STEP;
};

function mapStateToProps(state, ownProps) {
  const { schedule } = ownProps;
  const { scheduleId, spots } = schedule;
  const { classpassTotal: classpassSpots } = spots || {};
  const formName = `schedule-detail-update-network-cap-${scheduleId}`;

  return {
    ...ownProps,
    userId: selectUserId(state),
    venueId: selectActiveVenueId(state),
    displaySpotManagement: selectUserHasPermission(state, PERMISSION.DISPLAY_SPOT_MANAGEMENT),
    isDesktop: selectIsDesktop(state),
    name: formName,
    initialValues: {
      classpassSpots: classpassSpots || 0,
      actedOnOpportunity: false,
    },
    currentValues: getFormValues(formName)(state),
  };
}

const mapDispatchToProps = {
  handleSSAddSpot: addSpot,
  updateScheduleNetworkAllocation: updateScheduleNetworkAllocationAction,
  setField: change,
};

function mergeProps(stateProps, dispatchProps, ownProps) {
  const {
    venueId,
    schedule,
    isDesktop,
    name: formName,
    classpassSpots: initialClasspassSpots,
    currentValues: { classpassSpots: currentClasspassSpots = initialClasspassSpots } = {},
  } = stateProps;
  const { handleSSAddSpot, setField, updateScheduleNetworkAllocation } = dispatchProps;
  const { scheduleId, smartspotEnabled, opportunity = {} } = schedule;
  const { recommendation = 0 } = opportunity;

  return {
    ...stateProps,
    ...ownProps,
    decrementClasspassSpots: setField.bind(
      null,
      formName,
      'classpassSpots',
      decrementClasspassSpots(currentClasspassSpots)
    ),
    incrementClasspassSpots: setField.bind(
      null,
      formName,
      'classpassSpots',
      incrementClasspassSpots(currentClasspassSpots)
    ),
    actOnOpportunity: () => {
      setField(formName, 'classpassSpots', currentClasspassSpots + recommendation);
      setField(formName, 'actedOnOpportunity', true);
    },
    handleSSAddSpot: () =>
      handleSSAddSpot(currentClasspassSpots, scheduleId, currentClasspassSpots + SS_ADD_SPOT_VALUE, smartspotEnabled),
    onSubmit: (values, formAsyncFns) => {
      const { actedOnOpportunity } = values;
      const { resolveSubmit } = formAsyncFns;
      const data = {
        ...values,
        schedule,
        opportunities: opportunity ? [opportunity] : [],
        spotsAdded: recommendation,
        smartspotEnabled,
      };
      const args = [venueId, data, isDesktop, formName, formAsyncFns];

      if (isDesktop || actedOnOpportunity) {
        updateScheduleNetworkAllocation(...args);
      } else {
        resolveSubmit(); // don't lock the form while submitting
        debouncedSubmit(updateScheduleNetworkAllocation, ...args);
      }
    },
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
  withForm({
    destroyOnUnmount: false, // Fix for resizing the browser causing the form to unmount, erasing the form data
    enableReinitialize: true,
    onChange: (values, dispatch, props) => {
      const { classpassSpots } = values;
      const {
        name,
        initialValues: { classpassSpots: initialClasspassSpots },
      } = props;
      if (classpassSpots === null) return;
      if (classpassSpots === initialClasspassSpots) return;
      dispatch(submit(name));
    },
  })
)(ReservationsOverview);
